import { useNavigate } from 'react-router-dom';
import { formatDate, formatPrice } from '../../lib/constants';
import type { SessionDetails } from '../../types/session';
import { Star, Clock, Wind, DollarSign, Calendar, Scale, Tag, ChevronRight } from 'lucide-react';

interface SessionCardProps {
    session: SessionDetails;
    onClick?: () => void;
}

export default function SessionCard({ session, onClick }: SessionCardProps) {
    const navigate = useNavigate();

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else {
            navigate(`/edit-session/${session.id}`);
        }
    };

    return (
        <div
            className="group card bg-dark-surface/40 hover:bg-dark-surface/80 border-dark-border/60 hover:border-accent-purple/30 transition-all duration-300 cursor-pointer active:scale-[0.98]"
            onClick={handleClick}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0 pr-4">
                    <h3 className="font-black text-lg tracking-tight truncate leading-none italic group-hover:text-white transition-colors">
                        {session.strain_name || 'Mystery Strain'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {session.brand_name && (
                            <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-accent-purple/70 bg-accent-purple/5 px-2 py-0.5 rounded-md border border-accent-purple/10">
                                <Tag size={8} strokeWidth={3} /> {session.brand_name}
                            </span>
                        )}
                        <span className="text-[9px] font-bold text-dark-muted uppercase tracking-wider">
                            {session.product_type}
                        </span>
                        {session.thc_percent && (
                            <span className="text-[9px] font-black text-dark-muted/60 uppercase tracking-tighter">
                                {session.thc_percent}% THC
                            </span>
                        )}
                    </div>
                </div>
                <div className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest backdrop-blur-sm ${session.value_score >= 50
                        ? 'bg-accent-green/10 border-accent-green/20 text-accent-green'
                        : session.value_score >= 25
                            ? 'bg-accent-yellow/10 border-accent-yellow/20 text-accent-yellow'
                            : 'bg-accent-red/10 border-accent-red/20 text-accent-red'
                    }`}>
                    Value {Math.round(session.value_score)}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                    { icon: Star, label: `Rating: ${session.overall_rating}/10`, color: 'text-accent-yellow' },
                    { icon: Clock, label: session.time_of_day || 'Unknown Time', color: 'text-accent-purple' },
                    { icon: Wind, label: session.consumption_method || session.product_type, color: 'text-blue-400' },
                    { icon: DollarSign, label: formatPrice(session.session_cost || session.price_paid || 0), color: 'text-emerald-500' }
                ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-dark-bg/40 p-2 rounded-xl border border-dark-border/40">
                        <item.icon size={12} className={`${item.color} shrink-0`} strokeWidth={2.5} />
                        <span className="text-[10px] font-bold text-dark-text truncate uppercase tracking-tight">{item.label}</span>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between text-[9px] font-bold text-dark-muted uppercase tracking-widest pt-3 border-t border-dark-border/40">
                <div className="flex items-center gap-1.5">
                    <Calendar size={10} strokeWidth={2.5} />
                    <span>{formatDate(session.date_used)}</span>
                </div>
                {session.dosage_amount && (
                    <div className="flex items-center gap-1.5">
                        <Scale size={10} strokeWidth={2.5} />
                        <span>{session.dosage_amount}{session.dosage_unit} used</span>
                    </div>
                )}
            </div>

            {session.notes && (
                <div className="mt-3 relative pl-3 group-hover:pl-4 transition-all overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent-purple/20 group-hover:bg-accent-purple/50 rounded-full" />
                    <p className="text-xs text-dark-muted leading-relaxed line-clamp-2 italic font-medium">
                        "{session.notes}"
                    </p>
                </div>
            )}

            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0">
                <ChevronRight size={16} className="text-accent-purple/50" />
            </div>
        </div >
    );
}
